import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshBalance } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("timeout"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refreshBalance(); return; }
      } catch (e) { /* keep trying */ }
      tries.current += 1;
      if (tries.current >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshBalance]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="payment-success">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center">
        {state === "checking" && (<><Loader2 className="mx-auto h-10 w-10 animate-spin text-[#0B5D3B] dark:text-[#25D366]" /><h1 className="mt-4 font-display text-2xl font-bold">Confirming your payment…</h1><p className="mt-2 text-muted-foreground">This only takes a moment.</p></>)}
        {state === "paid" && (<><CheckCircle2 className="mx-auto h-12 w-12 text-[#059669]" /><h1 className="mt-4 font-display text-2xl font-bold">Payment complete!</h1><p className="mt-2 text-muted-foreground">Your tokens have been added.</p><Link to="/app" className="mt-6 inline-block rounded-full bg-[#0B5D3B] px-6 py-2.5 font-semibold text-white hover:bg-[#08472D]" data-testid="success-continue">Back to dashboard</Link></>)}
        {state === "timeout" && (<><Loader2 className="mx-auto h-10 w-10 text-amber-500" /><h1 className="mt-4 font-display text-2xl font-bold">Still processing</h1><p className="mt-2 text-muted-foreground">Your payment is being confirmed. Your balance will update shortly.</p><Link to="/app" className="mt-6 inline-block rounded-full bg-[#0B5D3B] px-6 py-2.5 font-semibold text-white hover:bg-[#08472D]">Back to dashboard</Link></>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="payment-cancel">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center">
        <XCircle className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 font-display text-2xl font-bold">Checkout cancelled</h1>
        <p className="mt-2 text-muted-foreground">No charge was made. You can try again anytime.</p>
        <button onClick={() => navigate("/app/billing")} className="mt-6 rounded-full bg-[#0B5D3B] px-6 py-2.5 font-semibold text-white hover:bg-[#08472D]" data-testid="cancel-retry">Back to billing</button>
      </div>
    </div>
  );
}
